import type { Concept } from '@yuan-math/curriculum';

/**
 * The Grade 12 calculus pathway.
 *
 * Precalculus ends by naming the limit and taking exactly one of them —
 * `rates-of-change` computes the slope of a tangent from first principles and
 * stops. That is the right place for a precalculus course to stop and the wrong
 * place for a student to stop, because everything the limit was built for is
 * one step past it.
 *
 * These ten concepts are that step, and they are a *pathway* rather than a
 * continuation of the year: `course-precalculus` and `course-calculus` both
 * start from `unit-limits`, and a student who wants the vectors, the conics and
 * the counting arguments takes one, while a student heading for an AP or A-level
 * paper takes the other. Neither is a prerequisite of the other, and the graph
 * says so — nothing here depends on `conic-sections`, `vectors`,
 * `mathematical-induction` or `binomial-theorem`. (Everything here depends on
 * `series`, but transitively, through `limits`: the infinite geometric sum is
 * where the limit is first taken, and sigma notation is where a Riemann sum is
 * first writable.)
 *
 * The orderings that are decisions rather than conventions:
 *
 * `continuity` comes before `derivatives` and after `limits`, and it is where
 * one-sided limits, infinite limits and limits at infinity are actually taught.
 * `limits` introduces the notation on a function with a hole, which is the
 * gentlest possible first case; every *interesting* limit fails in one of three
 * ways instead, and a student who has only seen the hole reads "the limit does
 * not exist" as a synonym for "undefined". Continuity is the vocabulary for the
 * three failures, and the reason the derivative can assume none of them happen.
 *
 * `derivatives` depends on `rates-of-change` and not merely on `limits`. The
 * derivative is the difference quotient's limit, and a student who meets it as
 * a rule — "bring the power down" — has been handed the answer to a question
 * they were never asked. The power rule in this concept is *derived* from the
 * difference quotient for n = 2 and n = 3 before it is stated.
 *
 * `implicit-differentiation` carries related rates, because they are the same
 * calculation. Differentiating x² + y² = 25 with respect to x and
 * differentiating it with respect to t differ in which letter is called the
 * independent one, and nothing else. Taught apart, related rates becomes a
 * genre of word problem with its own recipe; taught together, it is one idea
 * used twice.
 *
 * `optimization` depends on `derivative-applications` rather than directly on
 * `derivatives`. Finding where f′ = 0 is not optimisation — it locates the
 * candidates. Deciding which one is the maximum, and whether an endpoint beats
 * all of them, is what `derivative-applications` teaches, and an optimisation
 * answer given without it is a stationary point reported as an extremum.
 *
 * `motion-derivatives` sits beside optimisation rather than before it because
 * velocity and acceleration are the derivative used twice, and the second
 * derivative is exactly what the extremum test needs. The two concepts are the
 * same mathematics facing physics and facing a fence around a field.
 *
 * `antiderivatives` comes before `integrals`, which is the reverse of the
 * historical order and the reverse of some courses. The alternative is to build
 * the definite integral as a limit of Riemann sums and only then reveal that
 * antiderivatives evaluate it — which is honest, and leaves a student computing
 * nothing but limits of sums for a fortnight. Here the reversal is undone by
 * `fundamental-theorem`, which is stated as the *surprise* it is: the area and
 * the reversed derivative were never obviously the same thing.
 */
export const CALCULUS_CONCEPTS: Concept[] = [
  /* ----------------------------------------------- the limit, in full */
  {
    id: 'continuity',
    title: 'Continuity and One-Sided Limits',
    summary: 'The three ways a limit can fail, and the functions where none of them happen.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['limits', 'piecewise-functions'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ------------------------------------------------------- derivatives */
  {
    id: 'derivatives',
    title: 'The Derivative',
    summary: 'The slope of a curve at every point at once, as a function in its own right.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['rates-of-change', 'continuity'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'derivative-rules',
    title: 'Product, Quotient and Chain',
    summary: 'Differentiating things built out of other things — and why none of the three rules is the obvious guess.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivatives', 'function-operations'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'implicit-differentiation',
    title: 'Implicit Differentiation and Related Rates',
    summary: 'Differentiating an equation that was never solved for y, and the moving problems that turns out to answer.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivative-rules'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ------------------------------------- what a derivative is good for */
  {
    id: 'derivative-applications',
    title: 'Shape from the Derivative',
    summary: 'Where a curve rises, where it turns, and how to sketch one you have never seen.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivatives'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'optimization',
    title: 'Optimisation',
    summary: 'The largest volume, the cheapest box, the shortest path — questions with a best answer, found rather than guessed.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivative-applications'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'motion-derivatives',
    title: 'Velocity and Acceleration',
    summary: 'Position, its rate of change, and the rate of change of that — the derivative taken twice.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivative-applications'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* --------------------------------------------------------- integrals */
  {
    id: 'antiderivatives',
    title: 'Antiderivatives',
    summary: 'Running differentiation backwards, and the constant that can never be recovered.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['derivative-rules'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'integrals',
    title: 'Area Under a Curve',
    summary: 'Adding up infinitely many rectangles of no width, and getting an exact area.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['antiderivatives'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'fundamental-theorem',
    title: 'The Fundamental Theorem of Calculus',
    summary: 'The reason the area under a curve can be found by undoing a derivative — which is not obvious and was not obvious for two thousand years.',
    strand: 'calculus',
    levels: ['grade-12', 'university'],
    prerequisites: ['integrals'],
    status: 'available',
    estimatedMinutes: 45,
  },
];
